import { X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { PostCreateComposer } from './PostCreateComposer';

export function CreatePostModal({
  onClose,
  visible,
}: {
  onClose: () => void;
  visible: boolean;
}) {
  const [sessionKey, setSessionKey] = useState(0);
  const sheetProgressRef = useRef(new Animated.Value(0));
  const sheetProgress = sheetProgressRef.current;

  useEffect(() => {
    if (visible) {
      setSessionKey(Date.now());
      Animated.timing(sheetProgress, {
        duration: 210,
        easing: Easing.out(Easing.cubic),
        toValue: 1,
        useNativeDriver: true,
      }).start();
      return;
    }

    sheetProgress.setValue(0);
  }, [sheetProgress, visible]);

  const sheetMotionStyle = {
    opacity: sheetProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.88, 1],
    }),
    transform: [
      {
        translateY: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [28, 0],
        }),
      },
      {
        scale: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [0.985, 1],
        }),
      },
    ],
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.keyboardAvoider}
        >
          <Animated.View style={[styles.sheet, sheetMotionStyle]}>
            <View style={styles.header}>
              <View style={styles.headerSpacer} />
              <Text style={styles.title}>Create post</Text>
              <Pressable
                accessibilityLabel="Close create post"
                accessibilityRole="button"
                onPress={onClose}
                style={styles.closeButton}
              >
                <X color={colors.text} size={22} strokeWidth={2.5} />
              </Pressable>
            </View>
            {visible ? (
              <PostCreateComposer
                key={sessionKey}
                onCancel={onClose}
                onComplete={onClose}
                presentation="modal"
              />
            ) : null}
          </Animated.View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 21,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  header: {
    alignItems: 'center',
    borderBottomColor: colors.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  headerSpacer: {
    height: 42,
    width: 42,
  },
  keyboardAvoider: {
    maxHeight: '92%',
    maxWidth: 620,
    width: '100%',
  },
  sheet: {
    backgroundColor: colors.surface,
    borderRadius: 18,
    maxHeight: '100%',
    overflow: 'hidden',
    flexShrink: 1,
    width: '100%',
  },
  title: {
    color: colors.text,
    flex: 1,
    fontSize: 20,
    fontWeight: '900',
    textAlign: 'center',
  },
});
